// src/components/GestaoPatio.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaCar, FaMapMarkerAlt, FaRegCalendarCheck, FaRegCalendarTimes,
    FaSearch, FaRegCheckCircle, FaBan, FaTruck, FaPlus, FaTrash,
    FaSave, FaTimes, FaPencilAlt, FaInfoCircle, FaExternalLinkAlt,
    FaIdCard, FaBuilding, FaSpinner, FaMinus, FaExclamationTriangle,
    FaHistory, FaChevronDown, FaChevronUp, FaFireAlt, FaParking,
    FaClock,
} from 'react-icons/fa';
import { format, formatDistanceToNow, parseISO, differenceInDays } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useAuth } from '../hooks/useAuth';
import './GestaoPatio.css';

/* ─────────────────────────────────────────────────────────────
   UTILIDADES
   ───────────────────────────────────────────────────────────── */

const fmtCurrency = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 'R$ 0,00' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/**
 * ★ IDEIA 1 — Sistema de Envelhecimento Visual
 * Calcula dias no pátio e retorna classe CSS + label + urgência.
 * Funciona 100% no frontend com dados já existentes (data_checkin).
 */
const getAgingInfo = (dataCheckin) => {
    if (!dataCheckin) return null;
    const days = differenceInDays(new Date(), parseISO(dataCheckin));
    if (days >= 6) return { days, cls: 'aging-critical',  label: `${days}d`, urgente: true  };
    if (days >= 4) return { days, cls: 'aging-warning',   label: `${days}d`, urgente: false };
    if (days >= 2) return { days, cls: 'aging-attention', label: `${days}d`, urgente: false };
    return            { days, cls: 'aging-fresh',    label: days === 0 ? 'hoje' : `${days}d`, urgente: false };
};

/* ─────────────────────────────────────────────────────────────
   MODAL DE CONFIRMAÇÃO
   ───────────────────────────────────────────────────────────── */
const ConfirmModal = ({ title, message, onConfirm, onCancel, confirmText = 'Confirmar', cancelText = 'Cancelar', isProcessing = false, danger = false }) => (
    <div className="patio-modal-overlay">
        <div className="patio-modal-content small-modal">
            <h3>{title}</h3>
            <p>{message}</p>
            <div className="patio-modal-actions">
                <button onClick={onCancel} className="btn-cancel" disabled={isProcessing}>{cancelText}</button>
                <button onClick={onConfirm} className={`btn-confirm ${danger ? 'btn-confirm-danger' : ''}`} disabled={isProcessing}>
                    {isProcessing ? <FaSpinner className="spin" /> : confirmText}
                </button>
            </div>
        </div>
    </div>
);

/* ─────────────────────────────────────────────────────────────
   MODAL ADICIONAR VAGA (substitui o prompt() antigo)
   ───────────────────────────────────────────────────────────── */
const AddVagaModal = ({ onClose, onSuccess }) => {
    const [nome, setNome]     = useState('');
    const [tipo, setTipo]     = useState('Padrão');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!nome.trim()) return toast.warn('Informe o nome da vaga.');
        setSaving(true);
        try {
            const { data } = await api.post('/api/patio/vagas', { nome: nome.trim().toUpperCase(), tipo });
            toast.success(`Vaga "${data.nome}" adicionada!`);
            onSuccess(data.id);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao adicionar vaga.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="patio-modal-overlay">
            <div className="patio-modal-content small-modal">
                <button className="modal-close-btn" onClick={onClose}>&times;</button>
                <h3><FaPlus /> Nova Vaga</h3>
                <form onSubmit={handleSubmit}>
                    <div className="patio-form-group">
                        <label>Nome *</label>
                        <input type="text" value={nome} onChange={e => setNome(e.target.value)}
                            placeholder="Ex: A-01, M-05, P-10" autoFocus required />
                    </div>
                    <div className="patio-form-group">
                        <label>Tipo</label>
                        <select value={tipo} onChange={e => setTipo(e.target.value)}>
                            <option>Padrão</option>
                            <option>Moto</option>
                            <option>Pesado</option>
                        </select>
                    </div>
                    <div className="patio-modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancelar</button>
                        <button type="submit" className="btn-confirm" disabled={saving}>
                            {saving ? <><FaSpinner className="spin" /> Adicionando...</> : <><FaSave /> Adicionar</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   MODAL CHECK-IN
   ───────────────────────────────────────────────────────────── */
const CheckinModal = ({ vagasLivres, onClose, onCheckinSuccess }) => {
    const [searchTerm,    setSearchTerm]    = useState('');
    const [osResults,     setOsResults]     = useState([]);
    const [isLoadingOs,   setIsLoadingOs]   = useState(false);
    const [selectedOs,    setSelectedOs]    = useState(null);
    const [selectedVagaId, setSelectedVagaId] = useState('');
    const [placaManual,   setPlacaManual]   = useState('');
    const [observacoes,   setObservacoes]   = useState('');
    const [isCheckingIn,  setIsCheckingIn]  = useState(false);

    const fetchOs = useCallback(async (query) => {
        setIsLoadingOs(true);
        try {
            const { data } = await api.get('/api/patio/os-para-checkin', { params: { query } });
            setOsResults(data);
        } catch {
            toast.error('Falha ao buscar OS.');
        } finally {
            setIsLoadingOs(false);
        }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchOs(''); }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            if (searchTerm.length >= 2 || searchTerm === '') fetchOs(searchTerm);
            else setOsResults([]);
        }, 500);
        return () => clearTimeout(t);
    }, [searchTerm, fetchOs]);

    const handleSelectOs = (os) => {
        setSelectedOs(os);
        setPlacaManual(os.placa_veiculo || '');
        setOsResults([]);
        setSearchTerm(`OS #${os.id} (${os.nome_cliente || 'Sem Cliente'})`);
    };

    const handleConfirm = async () => {
        if (!selectedOs || !selectedVagaId) return toast.warn('Selecione uma OS e uma vaga.');
        const placaFinal = placaManual || selectedOs?.placa_veiculo;
        if (!placaFinal) return toast.warn('Informe a placa do veículo.');
        setIsCheckingIn(true);
        try {
            await api.post('/api/patio/check-in', {
                os_id: selectedOs.id, vaga_id: selectedVagaId,
                placa: placaFinal, observacoes, fotos_avarias: []
            });
            const vagaNome = vagasLivres.find(v => v.id === parseInt(selectedVagaId))?.nome || '';
            toast.success(`Check-in realizado! OS #${selectedOs.id} → Vaga ${vagaNome}`);
            onCheckinSuccess();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao registrar entrada.');
        } finally {
            setIsCheckingIn(false);
        }
    };

    return (
        <div className="patio-modal-overlay">
            <div className="patio-modal-content large-modal checkin-modal">
                <button className="modal-close-btn simple-close" onClick={onClose}>&times;</button>
                <h3><FaRegCalendarCheck /> Registrar Entrada no Pátio</h3>
                <div className="checkin-grid">
                    <div className="checkin-col">
                        <h4>1. Ordem de Serviço</h4>
                        <div className="patio-search-bar os-search">
                            <FaSearch />
                            <input type="text" placeholder="ID da OS, Placa ou Cliente..."
                                value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setSelectedOs(null); }} />
                        </div>
                        {isLoadingOs && <p className="os-loading"><FaSpinner className="spin" /> Buscando...</p>}
                        <ul className="os-results-list">
                            {osResults.map(os => (
                                <li key={os.id} onClick={() => handleSelectOs(os)}>
                                    <strong>OS #{os.id}</strong>
                                    <span className="os-status-pill">{os.status}</span>
                                    <br />
                                    <small>{os.nome_cliente || 'Cliente não informado'} · {os.placa_veiculo || 'Sem placa'}</small>
                                </li>
                            ))}
                            {searchTerm && !isLoadingOs && osResults.length === 0 && <li className="os-empty">Nenhuma OS elegível.</li>}
                            {!searchTerm && !isLoadingOs && osResults.length === 0 && <li className="os-empty">Sem OS disponíveis no momento.</li>}
                        </ul>
                        {selectedOs && (
                            <div className="selected-os-info">
                                <FaRegCheckCircle /> OS #{selectedOs.id} — {selectedOs.nome_cliente || 'S/C'}
                            </div>
                        )}
                    </div>
                    <div className="checkin-col">
                        <h4>2. Detalhes da Entrada</h4>
                        <div className="patio-form-group">
                            <label>Vaga Livre *</label>
                            <select value={selectedVagaId} onChange={e => setSelectedVagaId(e.target.value)} required>
                                <option value="">Selecione a vaga...</option>
                                {vagasLivres.map(v => <option key={v.id} value={v.id}>{v.nome} ({v.tipo || 'Padrão'})</option>)}
                            </select>
                        </div>
                        <div className="patio-form-group">
                            <label>Placa do Veículo *</label>
                            <input type="text" value={placaManual}
                                onChange={e => setPlacaManual(e.target.value.toUpperCase())}
                                placeholder="AAA-1234 ou ABC1D23" required />
                        </div>
                        <div className="patio-form-group">
                            <label>Observações</label>
                            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
                                rows="3" placeholder="Avarias, anotações..." />
                        </div>
                    </div>
                </div>
                <div className="patio-modal-actions">
                    <button className="btn-cancel" onClick={onClose} disabled={isCheckingIn}>Cancelar</button>
                    <button className="btn-confirm" onClick={handleConfirm}
                        disabled={!selectedOs || !selectedVagaId || !placaManual || isCheckingIn}>
                        {isCheckingIn
                            ? <><FaSpinner className="spin" /> Registrando...</>
                            : <><FaRegCalendarCheck /> Confirmar Check-in</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   ★ IDEIA 1 — Banner de Alertas de Permanência
   Mostra veículos com ≥ 3 dias no pátio, ordenados pelo mais
   antigo primeiro. Clicável para navegar à vaga correspondente.
   Sem nova rota — calculado com data_checkin já existente.
   ───────────────────────────────────────────────────────────── */
const AlertasBanner = ({ alertas, onVagaClick, expanded, onToggle }) => {
    if (alertas.length === 0) return null;
    const hasCritical = alertas.some(v => getAgingInfo(v.veiculo?.data_checkin)?.days >= 6);

    return (
        <div className={`alertas-banner ${hasCritical ? 'alertas-banner--critical' : 'alertas-banner--warning'}`}>
            <button className="alertas-banner-header" onClick={onToggle}>
                <span className="alertas-banner-title">
                    <FaFireAlt />
                    <strong>{alertas.length} veículo{alertas.length > 1 ? 's' : ''}</strong> com permanência {hasCritical ? 'crítica' : 'prolongada'}
                    {hasCritical && <span className="alertas-pulse-badge">Ação necessária</span>}
                </span>
                {expanded ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {expanded && (
                <div className="alertas-banner-list">
                    {alertas.map(vaga => {
                        const aging = getAgingInfo(vaga.veiculo?.data_checkin);
                        return (
                            <button key={vaga.id} className={`alerta-item ${aging?.cls}`} onClick={() => onVagaClick(vaga)}>
                                <span className="alerta-placa"><FaCar /> {vaga.veiculo?.placa || 'S/P'}</span>
                                <span className="alerta-vaga">{vaga.nome}</span>
                                <span className="alerta-cliente">{vaga.veiculo?.nome_cliente || '—'}</span>
                                <span className={`alerta-dias-badge ${aging?.cls}`}>{aging?.days}d</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   ★ IDEIA 2 — Feed de Atividade Recente
   Exibido no painel direito quando nenhuma vaga está selecionada.
   Requer GET /api/patio/historico (ver nota no topo do arquivo
   sobre a rota a adicionar em routesModules.js).
   Falha silenciosa se a rota ainda não existir.
   ───────────────────────────────────────────────────────────── */
const HistoricoFeed = ({ historico, loading }) => (
    <div className="historico-feed">
        <div className="historico-feed-title"><FaHistory /> Atividade Recente</div>
        {loading ? (
            <div className="historico-empty"><FaSpinner className="spin" /></div>
        ) : historico.length === 0 ? (
            <div className="historico-empty">
                <FaHistory />
                <p>Nenhuma movimentação registrada.</p>
            </div>
        ) : (
            <ul className="historico-list">
                {historico.map((item, idx) => {
                    const isCheckin  = item.status === 'no_patio';
                    const dateField  = item.data_checkout || item.data_checkin;
                    return (
                        <li key={`${item.id}-${idx}`} className={`historico-item ${isCheckin ? 'checkin' : 'checkout'}`}>
                            <div className="historico-icon">
                                {isCheckin ? <FaRegCalendarCheck /> : <FaRegCalendarTimes />}
                            </div>
                            <div className="historico-info">
                                <strong>{item.placa || 'S/P'}</strong>
                                <span>{isCheckin ? 'Entrada' : 'Saída'}{item.vaga_nome ? ` · ${item.vaga_nome}` : ''}</span>
                                {item.nome_cliente && <small>{item.nome_cliente}</small>}
                            </div>
                            <div className="historico-time">
                                <FaClock />
                                {dateField
                                    ? formatDistanceToNow(parseISO(dateField), { locale: ptBR, addSuffix: true })
                                    : '—'}
                            </div>
                        </li>
                    );
                })}
            </ul>
        )}
    </div>
);

/* ─────────────────────────────────────────────────────────────
   COMPONENTE PRINCIPAL
   ───────────────────────────────────────────────────────────── */
export default function GestaoPatio() {
    const { perfil }     = useAuth();
    const isAdmin        = useMemo(() => perfil === 'admin_geral' || perfil === 'admin', [perfil]);
    const isAdminGeral   = useMemo(() => perfil === 'admin_geral', [perfil]);

    /* ── State ─────────────────────────────────────────────── */
    const [vagas,              setVagas]              = useState([]);
    const [historico,          setHistorico]          = useState([]);
    const [isLoading,          setIsLoading]          = useState(true);
    const [loadingHistorico,   setLoadingHistorico]   = useState(false);
    const [error,              setError]              = useState(null);
    const [selectedVaga,       setSelectedVaga]       = useState(null);
    const [filtro,             setFiltro]             = useState('');
    const [filterTab,          setFilterTab]          = useState('todas');
    const [alertasExpanded,    setAlertasExpanded]    = useState(true);
    const [showConfirmCheckout, setShowConfirmCheckout] = useState(null);
    const [isCheckingOut,      setIsCheckingOut]      = useState(false);
    const [editMode,           setEditMode]           = useState(false);
    const [editingVaga,        setEditingVaga]        = useState({ id: null, nome: '', tipo: '' });
    const [showConfirmDelete,  setShowConfirmDelete]  = useState(null);
    const [isDeleting,         setIsDeleting]         = useState(false);
    const [showCheckinModal,   setShowCheckinModal]   = useState(false);
    const [showAddVagaModal,   setShowAddVagaModal]   = useState(false);

    /* ── Computed ──────────────────────────────────────────── */
    const vagasLivres      = useMemo(() => vagas.filter(v => v.status === 'livre' && v.id > 0), [vagas]);
    const vagasOcupadas    = useMemo(() => vagas.filter(v => v.status === 'ocupada'), [vagas]);
    const totalReais       = useMemo(() => vagas.filter(v => v.id > 0).length, [vagas]);
    const ocupacaoPct      = totalReais > 0 ? ((vagasOcupadas.length / totalReais) * 100).toFixed(1) : 0;

    const vagasEmAlerta = useMemo(() =>
        vagasOcupadas
            .filter(v => v.veiculo?.data_checkin && differenceInDays(new Date(), parseISO(v.veiculo.data_checkin)) >= 3)
            .sort((a, b) =>
                differenceInDays(new Date(), parseISO(b.veiculo.data_checkin)) -
                differenceInDays(new Date(), parseISO(a.veiculo.data_checkin))
            ),
        [vagasOcupadas]
    );

    const vagasFiltradas = useMemo(() => {
        const term = filtro.toLowerCase();
        let result = vagas.filter(v =>
            (v.nome?.toLowerCase()  || '').includes(term) ||
            (v.tipo?.toLowerCase()  || '').includes(term) ||
            (v.veiculo?.placa?.toLowerCase()        || '').includes(term) ||
            (v.veiculo?.nome_cliente?.toLowerCase() || '').includes(term)
        );
        if (filterTab === 'ocupadas') return result.filter(v => v.status === 'ocupada');
        if (filterTab === 'livres')   return result.filter(v => v.status === 'livre');
        if (filterTab === 'alertas')  return result.filter(v =>
            v.status === 'ocupada' && v.veiculo?.data_checkin &&
            differenceInDays(new Date(), parseISO(v.veiculo.data_checkin)) >= 3
        );
        return result;
    }, [vagas, filtro, filterTab]);

    /* ── Fetch ─────────────────────────────────────────────── */
    const fetchPatioData = useCallback(async (selectVagaId = null) => {
        if (vagas.length === 0) setIsLoading(true);
        setError(null);
        try {
            const { data } = await api.get('/api/patio/dashboard');
            setVagas(data);
            if (selectVagaId) {
                setSelectedVaga(data.find(v => v.id === selectVagaId) || null);
            } else if (selectedVaga) {
                setSelectedVaga(data.find(v => v.id === selectedVaga.id) || null);
            }
        } catch (err) {
            console.error('Erro pátio:', err);
            setError('Falha ao carregar dados do pátio.');
            toast.error('Falha ao carregar dados do pátio.');
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVaga, vagas.length]);

    const fetchHistorico = useCallback(async () => {
        setLoadingHistorico(true);
        try {
            const { data } = await api.get('/api/patio/historico?limit=15');
            setHistorico(Array.isArray(data) ? data : []);
        } catch {
            setHistorico([]); // rota pode não existir ainda — falha silenciosa
        } finally {
            setLoadingHistorico(false);
        }
    }, []);

    useEffect(() => {
        fetchPatioData();
        fetchHistorico();
        const id = setInterval(() => { fetchPatioData(); fetchHistorico(); }, 60_000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Handlers ──────────────────────────────────────────── */
    const handleVagaClick = (vaga) => {
        setSelectedVaga(vaga);
        if (editMode && vaga.id > 0) setEditingVaga({ id: vaga.id, nome: vaga.nome, tipo: vaga.tipo || 'Padrão' });
        else setEditingVaga({ id: null, nome: '', tipo: '' });
    };

    const handleConfirmCheckout = async () => {
        if (!showConfirmCheckout) return;
        setIsCheckingOut(true);
        try {
            const { data } = await api.post(`/api/patio/check-out/${showConfirmCheckout}`);
            toast.success(`Veículo liberado! ${data.dias} diária(s) · ${fmtCurrency(data.totalDiarias)}`);
            setShowConfirmCheckout(null);
            setSelectedVaga(null);
            fetchPatioData();
            fetchHistorico();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao liberar veículo.');
        } finally {
            setIsCheckingOut(false);
        }
    };

    const handleToggleEditMode = () => {
        setEditMode(m => !m);
        setEditingVaga({ id: null, nome: '', tipo: '' });
        setSelectedVaga(null);
    };

    const handleEditVagaChange = (e) => setEditingVaga(ev => ({ ...ev, [e.target.name]: e.target.value }));

    const handleSaveVaga = async (e) => {
        e.preventDefault();
        if (!editingVaga.id || !editingVaga.nome) return toast.warn('Nome não pode ser vazio.');
        try {
            await api.put(`/api/patio/vagas/${editingVaga.id}`, { nome: editingVaga.nome, tipo: editingVaga.tipo });
            toast.success(`Vaga ${editingVaga.nome} atualizada!`);
            fetchPatioData(editingVaga.id);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao salvar.');
        }
    };

    const handleRemoveLastFreeVaga = () => {
        const last = [...vagas].sort((a, b) => b.id - a.id).find(v => v.id > 0 && v.status === 'livre');
        if (!last) return toast.warn('Nenhuma vaga livre para remover.');
        setShowConfirmDelete(last.id);
    };

    const handleDeleteVaga = async () => {
        if (!showConfirmDelete) return;
        setIsDeleting(true);
        try {
            await api.delete(`/api/patio/vagas/${showConfirmDelete}`);
            toast.success('Vaga excluída!');
            setShowConfirmDelete(null);
            setSelectedVaga(null);
            setEditingVaga({ id: null, nome: '', tipo: '' });
            fetchPatioData();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao excluir.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCheckinSuccess = () => { fetchPatioData(); fetchHistorico(); };

    /* ── Render ─────────────────────────────────────────────── */
    return (
        <div className={`patio-page-container ${editMode ? 'edit-mode-active' : ''}`}>

            {/* ── Modais ── */}
            {showConfirmCheckout && (
                <ConfirmModal
                    title="Confirmar Check-out"
                    message={`Liberar placa ${vagas.find(v => v.veiculo?.id === showConfirmCheckout)?.veiculo?.placa || 'N/A'}? As diárias serão calculadas e lançadas.`}
                    onConfirm={handleConfirmCheckout} onCancel={() => setShowConfirmCheckout(null)}
                    isProcessing={isCheckingOut} danger confirmText="Sim, Liberar"
                />
            )}
            {showConfirmDelete && (
                <ConfirmModal
                    title="Excluir Vaga"
                    message={`Excluir "${vagas.find(v => v.id === showConfirmDelete)?.nome || `#${showConfirmDelete}`}"? Irreversível.`}
                    onConfirm={handleDeleteVaga} onCancel={() => setShowConfirmDelete(null)}
                    isProcessing={isDeleting} danger confirmText="Sim, Excluir"
                />
            )}
            {showCheckinModal && (
                <CheckinModal vagasLivres={vagasLivres} onClose={() => setShowCheckinModal(false)} onCheckinSuccess={handleCheckinSuccess} />
            )}
            {showAddVagaModal && (
                <AddVagaModal onClose={() => setShowAddVagaModal(false)} onSuccess={(id) => fetchPatioData(id)} />
            )}

            {/* ── Cabeçalho ── */}
            <div className="patio-header">
                <div className="patio-header-left">
                    <div className="patio-header-icon-wrap"><FaParking /></div>
                    <div>
                        <h1>Gestão de Pátio</h1>
                        <p className="patio-subtitle">Monitoramento em tempo real · atualiza a cada minuto</p>
                    </div>
                </div>
                <div className="patio-header-right">
                    {isAdmin && (
                        <div className="admin-controls">
                            <button onClick={handleToggleEditMode} className={`btn-admin-mode ${editMode ? 'active' : ''}`}>
                                <FaPencilAlt /> {editMode ? 'Sair do modo edição' : 'Editar Layout'}
                            </button>
                            {editMode && (
                                <>
                                    <button className="btn-add-vaga" onClick={() => setShowAddVagaModal(true)}>
                                        <FaPlus /> Adicionar Vaga
                                    </button>
                                    {isAdminGeral && (
                                        <button className="btn-remove-vaga" onClick={handleRemoveLastFreeVaga} disabled={isDeleting}>
                                            {isDeleting ? <FaSpinner className="spin" /> : <FaMinus />} Remover
                                        </button>
                                    )}
                                </>
                            )}
                            {!editMode && (
                                <button className="btn-check-in-main" onClick={() => setShowCheckinModal(true)}>
                                    <FaRegCalendarCheck /> Registrar Entrada
                                </button>
                            )}
                        </div>
                    )}
                    <div className="patio-legend">
                        <span className="legend-item livre">Livre</span>
                        <span className="legend-item ocupada">Ocupada</span>
                        <span className="legend-item aging-attention-legend">2–3d</span>
                        <span className="legend-item aging-warning-legend">4–5d</span>
                        <span className="legend-item aging-critical-legend">6+d</span>
                        {editMode && <span className="legend-item edit-mode-indicator">Modo Edição</span>}
                    </div>
                </div>
            </div>

            {/* ── KPIs ── */}
            <div className="patio-kpi-row">
                <div className="patio-kpi-card kpi-total">
                    <div className="kpi-icon"><FaParking /></div>
                    <div className="kpi-body">
                        <span className="kpi-value">{totalReais}</span>
                        <span className="kpi-label">Total de Vagas</span>
                    </div>
                </div>
                <div className="patio-kpi-card kpi-ocupadas">
                    <div className="kpi-icon"><FaCar /></div>
                    <div className="kpi-body">
                        <span className="kpi-value">{vagasOcupadas.length}</span>
                        <span className="kpi-label">Ocupadas</span>
                    </div>
                </div>
                <div className="patio-kpi-card kpi-livres">
                    <div className="kpi-icon"><FaRegCheckCircle /></div>
                    <div className="kpi-body">
                        <span className="kpi-value">{vagasLivres.length}</span>
                        <span className="kpi-label">Disponíveis</span>
                    </div>
                </div>
                <div className="patio-kpi-card kpi-taxa">
                    <div className="kpi-icon kpi-taxa-ring">
                        <svg viewBox="0 0 36 36" className="taxa-svg">
                            <path className="taxa-track" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                            <path className="taxa-fill" strokeDasharray={`${ocupacaoPct}, 100`}
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                            <text x="18" y="20.35" className="taxa-text">{ocupacaoPct}%</text>
                        </svg>
                    </div>
                    <div className="kpi-body">
                        <span className="kpi-value">{ocupacaoPct}%</span>
                        <span className="kpi-label">Taxa de Ocupação</span>
                    </div>
                </div>
                <div className={`patio-kpi-card kpi-alertas ${vagasEmAlerta.length > 0 ? (vagasEmAlerta.some(v => getAgingInfo(v.veiculo?.data_checkin)?.days >= 6) ? 'kpi-alertas--critico' : 'kpi-alertas--atencao') : ''}`}>
                    <div className="kpi-icon"><FaFireAlt /></div>
                    <div className="kpi-body">
                        <span className="kpi-value">{vagasEmAlerta.length}</span>
                        <span className="kpi-label">Em Alerta (≥3d)</span>
                    </div>
                </div>
            </div>

            {/* ── ★ IDEIA 1: Banner de Alertas de Permanência ── */}
            <AlertasBanner
                alertas={vagasEmAlerta}
                onVagaClick={(vaga) => { handleVagaClick(vaga); setFilterTab('todas'); }}
                expanded={alertasExpanded}
                onToggle={() => setAlertasExpanded(e => !e)}
            />

            {/* ── Grid principal ── */}
            <div className="patio-main-grid">

                {/* Coluna esquerda: mapa */}
                <div className="patio-map-column">
                    <div className="patio-card map-controls-card">
                        <div className="patio-search-bar">
                            <FaSearch />
                            <input type="text" placeholder="Buscar vaga, placa, cliente..."
                                value={filtro} onChange={e => setFiltro(e.target.value)} />
                            {filtro && <button className="search-clear-btn" onClick={() => setFiltro('')}><FaTimes /></button>}
                        </div>
                        <div className="filter-tabs">
                            {[
                                { key: 'todas',    label: 'Todas',      count: vagas.length             },
                                { key: 'ocupadas', label: 'Ocupadas',   count: vagasOcupadas.length      },
                                { key: 'livres',   label: 'Disponíveis', count: vagasLivres.length       },
                                { key: 'alertas',  label: '⚠ Alertas',  count: vagasEmAlerta.length, danger: true },
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    className={`filter-tab ${filterTab === tab.key ? 'active' : ''} ${tab.danger && tab.count > 0 ? 'tab-danger' : ''}`}
                                    onClick={() => setFilterTab(tab.key)}
                                >
                                    {tab.label}
                                    <span className="filter-tab-count">{tab.count}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="patio-card grid-card">
                        {editMode && (
                            <div className="edit-mode-notice">
                                <FaPencilAlt /> Modo Edição — clique em uma vaga para editar
                            </div>
                        )}
                        {isLoading && <div className="patio-loading"><FaSpinner className="spin" /> Carregando mapa...</div>}
                        {error    && <p className="patio-error">{error}</p>}
                        {!isLoading && !error && (
                            <div className="patio-grid-container">
                                {vagasFiltradas.length === 0 && (
                                    <div className="empty-grid-msg"><FaSearch /> Nenhuma vaga encontrada.</div>
                                )}
                                {vagasFiltradas.map(vaga => {
                                    const aging = vaga.status === 'ocupada' && vaga.veiculo?.data_checkin
                                        ? getAgingInfo(vaga.veiculo.data_checkin) : null;
                                    const spotClass = [
                                        'vaga-spot',
                                        vaga.status,
                                        selectedVaga?.id === vaga.id ? 'selected' : '',
                                        vaga.id < 0 ? 'simulated' : '',
                                        aging ? aging.cls : '',
                                    ].filter(Boolean).join(' ');

                                    return (
                                        <div key={vaga.id} className={spotClass}
                                            onClick={() => handleVagaClick(vaga)}
                                            title={`${vaga.nome} · ${vaga.tipo || 'Padrão'}${aging ? ` · ${aging.days}d no pátio` : ''}`}
                                        >
                                            {editMode && editingVaga.id === vaga.id && vaga.id > 0 ? (
                                                <form onSubmit={handleSaveVaga} className="vaga-edit-form">
                                                    <input name="nome" value={editingVaga.nome}
                                                        onChange={handleEditVagaChange}
                                                        onClick={e => e.stopPropagation()} autoFocus />
                                                    <select name="tipo" value={editingVaga.tipo}
                                                        onChange={handleEditVagaChange}
                                                        onClick={e => e.stopPropagation()}>
                                                        <option>Padrão</option>
                                                        <option>Moto</option>
                                                        <option>Pesado</option>
                                                    </select>
                                                    <div className="vaga-edit-form-actions">
                                                        <button type="submit" onClick={e => e.stopPropagation()} title="Salvar"><FaSave /></button>
                                                        {isAdminGeral && vaga.status === 'livre' && (
                                                            <button type="button" className="delete-btn"
                                                                onClick={e => { e.stopPropagation(); setShowConfirmDelete(vaga.id); }}
                                                                title="Excluir"><FaTrash /></button>
                                                        )}
                                                        {isAdminGeral && vaga.status === 'ocupada' && (
                                                            <button type="button" className="delete-btn" disabled title="Vaga ocupada"><FaBan /></button>
                                                        )}
                                                    </div>
                                                </form>
                                            ) : (
                                                <>
                                                    <span className="vaga-nome">{vaga.nome}</span>
                                                    <span className="vaga-tipo-label">{vaga.tipo || 'Padrão'}</span>
                                                    {vaga.status === 'ocupada' && !editMode && (
                                                        <>
                                                            <FaCar className="vaga-car-icon" />
                                                            <span className="vaga-placa">{vaga.veiculo?.placa || 'N/A'}</span>
                                                            {aging && (
                                                                <span className={`vaga-aging-badge ${aging.cls}`}>{aging.label}</span>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Coluna direita: detalhes ou feed */}
                <div className="patio-details-column">
                    <div className="patio-card details-card">

                        {/* ★ IDEIA 2: Feed de Atividade (quando nada selecionado) */}
                        {!selectedVaga && !editMode && (
                            <HistoricoFeed historico={historico} loading={loadingHistorico} />
                        )}
                        {!selectedVaga && editMode && (
                            <div className="patio-details-empty">
                                <FaPencilAlt />
                                <p>Selecione uma vaga no mapa para editar.</p>
                            </div>
                        )}

                        {/* Vaga livre — normal */}
                        {selectedVaga && selectedVaga.status === 'livre' && !editMode && (
                            <div className="patio-details-content">
                                <div className="detail-header detail-header--livre">
                                    <FaRegCheckCircle />
                                    <div>
                                        <h3>Vaga {selectedVaga.nome}</h3>
                                        <span>{selectedVaga.tipo || 'Padrão'} · Disponível</span>
                                    </div>
                                    <button className="detail-close-btn" onClick={() => setSelectedVaga(null)}><FaTimes /></button>
                                </div>
                                <p className="detail-free-msg">Esta vaga está livre e pronta para receber um veículo.</p>
                                <button className="btn-check-in" onClick={() => setShowCheckinModal(true)}>
                                    <FaRegCalendarCheck /> Registrar Entrada Aqui
                                </button>
                                {selectedVaga.id < 0 && (
                                    <small className="simulated-warning">⚠ Vaga simulada — crie vagas reais no modo de edição.</small>
                                )}
                            </div>
                        )}

                        {/* Vaga livre — modo edição */}
                        {selectedVaga && selectedVaga.status === 'livre' && editMode && editingVaga.id === selectedVaga.id && selectedVaga.id > 0 && (
                            <div className="patio-details-content">
                                <div className="detail-header detail-header--edit">
                                    <FaPencilAlt />
                                    <div><h3>Editando Vaga</h3><span>{selectedVaga.nome}</span></div>
                                </div>
                                <form onSubmit={handleSaveVaga}>
                                    <div className="patio-form-group"><label>Nome da Vaga</label>
                                        <input type="text" name="nome" value={editingVaga.nome} onChange={handleEditVagaChange} required />
                                    </div>
                                    <div className="patio-form-group"><label>Tipo</label>
                                        <select name="tipo" value={editingVaga.tipo} onChange={handleEditVagaChange}>
                                            <option>Padrão</option><option>Moto</option><option>Pesado</option>
                                        </select>
                                    </div>
                                    <div className="vaga-edit-actions">
                                        <button type="submit" className="btn-save"><FaSave /> Salvar</button>
                                        {isAdminGeral && (
                                            <button type="button" className="btn-delete" onClick={() => setShowConfirmDelete(selectedVaga.id)}>
                                                <FaTrash /> Excluir
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Vaga simulada — modo edição */}
                        {selectedVaga && editMode && selectedVaga.id < 0 && (
                            <div className="patio-details-content">
                                <div className="detail-header detail-header--edit">
                                    <FaInfoCircle />
                                    <div><h3>Vaga Simulada</h3><span>{selectedVaga.nome}</span></div>
                                </div>
                                <p>Vaga simulada. Use "+ Adicionar Vaga" para criar vagas reais no banco de dados.</p>
                            </div>
                        )}

                        {/* Vaga ocupada — normal */}
                        {selectedVaga && selectedVaga.status === 'ocupada' && selectedVaga.veiculo && !editMode && (() => {
                            const aging = getAgingInfo(selectedVaga.veiculo.data_checkin);
                            return (
                                <div className="patio-details-content">
                                    <div className={`detail-header detail-header--ocupada ${aging?.cls || ''}`}>
                                        <FaCar />
                                        <div>
                                            <h3>Vaga {selectedVaga.nome}</h3>
                                            <span>
                                                {selectedVaga.tipo || 'Padrão'} · Ocupada
                                                {aging && <span className={`aging-inline-badge ${aging.cls}`}>{aging.label}</span>}
                                            </span>
                                        </div>
                                        <button className="detail-close-btn" onClick={() => setSelectedVaga(null)}><FaTimes /></button>
                                    </div>

                                    {aging && aging.days >= 3 && (
                                        <div className={`detail-aging-alert ${aging.cls}`}>
                                            <FaExclamationTriangle />
                                            Veículo há <strong>{aging.days} dia{aging.days !== 1 ? 's' : ''}</strong> no pátio.
                                            {aging.days >= 6 && <strong> Ação urgente necessária!</strong>}
                                        </div>
                                    )}

                                    <div className="info-group placa">
                                        <strong><FaIdCard /> Placa</strong>
                                        <span>{selectedVaga.veiculo.placa}</span>
                                    </div>
                                    <div className="info-group cliente">
                                        <strong><FaBuilding /> Cliente</strong>
                                        <span>
                                            {selectedVaga.veiculo.nome_cliente || 'N/A'}
                                            {selectedVaga.veiculo.cliente_telefone ? ` · ${selectedVaga.veiculo.cliente_telefone}` : ''}
                                        </span>
                                    </div>
                                    <div className="info-group os">
                                        <strong><FaExternalLinkAlt /> OS Origem</strong>
                                        <span>
                                            #{selectedVaga.veiculo.os_origem_id}
                                            <a href={`/chamado/${selectedVaga.veiculo.os_origem_id}`} target="_blank" rel="noopener noreferrer"> Ver OS</a>
                                        </span>
                                        <small>Status: {selectedVaga.veiculo.os_status || 'N/A'}</small>
                                    </div>
                                    <div className="info-group date">
                                        <strong><FaRegCalendarCheck /> Check-in</strong>
                                        <span>
                                            {selectedVaga.veiculo.data_checkin
                                                ? format(parseISO(selectedVaga.veiculo.data_checkin), 'dd/MM/yyyy HH:mm')
                                                : 'N/A'}
                                        </span>
                                    </div>
                                    <div className="info-group time">
                                        <strong><FaClock /> Tempo no Pátio</strong>
                                        <span>
                                            {selectedVaga.veiculo.data_checkin
                                                ? formatDistanceToNow(parseISO(selectedVaga.veiculo.data_checkin), { locale: ptBR, addSuffix: true })
                                                : 'N/A'}
                                        </span>
                                    </div>
                                    {selectedVaga.veiculo.observacoes && (
                                        <div className="info-group obs">
                                            <strong>Observações</strong>
                                            <p className="obs-text">{selectedVaga.veiculo.observacoes}</p>
                                        </div>
                                    )}
                                    <button className="btn-check-out"
                                        onClick={() => setShowConfirmCheckout(selectedVaga.veiculo.id)}
                                        disabled={isCheckingOut}>
                                        <FaRegCalendarTimes />
                                        {isCheckingOut
                                            ? <><FaSpinner className="spin" /> Processando...</>
                                            : 'Liberar Veículo (Check-out)'}
                                    </button>
                                </div>
                            );
                        })()}

                        {/* Vaga ocupada — modo edição */}
                        {selectedVaga && selectedVaga.status === 'ocupada' && editMode && (
                            <div className="patio-details-content">
                                <div className="detail-header detail-header--edit">
                                    <FaBan />
                                    <div><h3>Vaga {selectedVaga.nome} Ocupada</h3><span>Não editável enquanto ocupada</span></div>
                                </div>
                                <p>Placa <strong>{selectedVaga.veiculo?.placa}</strong> (OS #{selectedVaga.veiculo?.os_origem_id}).
                                    Saia do modo edição para liberar o veículo.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}